
import React, { useState } from 'react';
import { Plan, ServiceDefinition, User, Language, UserRole } from '../../types';
import { TRANSLATIONS } from '../../constants';

interface AdminPermissionsProps {
  language: Language;
  plans: Plan[];
  services: ServiceDefinition[];
}

// Mock users for the permission manager demo
const MOCK_USERS: User[] = [
  { id: 'u1', name: 'Ahmed Ali', email: 'ahmed@example.com', plan: 'free', role: 'user' },
  { id: 'u2', name: 'Sarah Smith', email: 'sarah@example.com', plan: 'pro', role: 'admin' },
  { id: 'u3', name: 'Tech Corp', email: 'admin@techcorp.sa', plan: 'enterprise', role: 'superadmin' },
];

const AdminPermissions: React.FC<AdminPermissionsProps> = ({ language, plans, services }) => {
  const isRtl = language === 'ar';
  const t = TRANSLATIONS[language];
  const [activeTab, setActiveTab] = useState<'matrix' | 'users'>('matrix');
  const [searchUser, setSearchUser] = useState('');
  
  // State for the Service/Plan Matrix
  const [permissionMatrix, setPermissionMatrix] = useState<Record<string, Record<string, boolean>>>(() => {
    const initial: Record<string, Record<string, boolean>> = {};
    services.forEach(s => {
      initial[s.id] = {
        'free': !s.isPro,
        'pro': true,
        'enterprise': true
      };
    });
    return initial;
  });

  const togglePermission = (serviceId: string, planId: string) => {
    setPermissionMatrix(prev => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        [planId]: !prev[serviceId][planId]
      }
    }));
  };

  const [users, setUsers] = useState(MOCK_USERS);

  const handleUserRoleChange = (userId: string, role: UserRole) => {
      setUsers(users.map(u => u.id === userId ? { ...u, role } : u));
  };

  const handleUserPlanChange = (userId: string, plan: 'free' | 'pro' | 'enterprise') => {
      setUsers(users.map(u => u.id === userId ? { ...u, plan } : u));
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      
      {/* Sub-Tabs */}
      <div className="flex bg-slate-200 dark:bg-white/5 p-1 rounded-xl w-full max-w-sm">
         <button 
           onClick={() => setActiveTab('matrix')}
           className={`flex-1 py-2 rounded-lg text-xs font-black transition-all ${activeTab === 'matrix' ? 'bg-white dark:bg-emerald-500 text-slate-800 dark:text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
         >
           {isRtl ? 'مصفوفة الخدمات' : 'Service Matrix'}
         </button>
         <button 
           onClick={() => setActiveTab('users')}
           className={`flex-1 py-2 rounded-lg text-xs font-black transition-all ${activeTab === 'users' ? 'bg-white dark:bg-emerald-500 text-slate-800 dark:text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
         >
           {isRtl ? 'صلاحيات المستخدمين' : 'User Roles'}
         </button>
      </div>

      {activeTab === 'matrix' && (
        <div className="bg-white dark:bg-[#1a1a20] rounded-[24px] border border-slate-200 dark:border-white/5 overflow-hidden shadow-sm">
           <div className="p-6 border-b border-slate-100 dark:border-white/5">
              <h3 className="text-lg font-black text-slate-800 dark:text-white">{isRtl ? 'التحكم في وصول الاشتراكات' : 'Subscription Access Control'}</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{isRtl ? 'حدد الخدمات المتاحة لكل خطة اشتراك.' : 'Define which services are accessible for each subscription plan.'}</p>
           </div>
           
           <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                 <thead>
                    <tr className="bg-slate-50 dark:bg-white/[0.02]">
                       <th className="p-4 text-xs font-black text-slate-400 uppercase tracking-wider sticky left-0 bg-slate-50 dark:bg-[#15151a] z-10 w-1/3">
                          {isRtl ? 'الخدمة' : 'Service'}
                       </th>
                       {plans.map(plan => (
                          <th key={plan.id} className="p-4 text-center">
                             <div className="flex flex-col items-center gap-1">
                                <div className={`w-8 h-8 rounded-lg bg-${plan.color || 'slate'}-500/10 flex items-center justify-center text-${plan.color || 'slate'}-500`}>
                                   <i className={`fas ${plan.icon}`}></i>
                                </div>
                                <span className={`text-[10px] font-black uppercase text-${plan.color || 'slate'}-500`}>{plan.name}</span>
                             </div>
                          </th>
                       ))}
                    </tr>
                 </thead>
                 <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                    {services.map(service => (
                       <tr key={service.id} className="hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors">
                          <td className="p-4 sticky left-0 bg-white dark:bg-[#1a1a20] group-hover:bg-slate-50 dark:group-hover:bg-[#1f1f25]">
                             <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${service.color} flex items-center justify-center text-white shadow-sm`}>
                                   <i className={`fas ${service.icon} text-xs`}></i>
                                </div>
                                <div>
                                   <p className="text-xs font-bold text-slate-800 dark:text-white">{service.title}</p>
                                   <p className="text-[9px] text-slate-400">{service.category}</p>
                                </div>
                             </div>
                          </td>
                          {plans.map(plan => {
                             const isEnabled = permissionMatrix[service.id]?.[plan.id];
                             return (
                                <td key={`${service.id}-${plan.id}`} className="p-4 text-center">
                                   <button 
                                      onClick={() => togglePermission(service.id, plan.id)}
                                      className={`
                                         w-12 h-6 rounded-full transition-all duration-300 relative
                                         ${isEnabled ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-white/10'}
                                      `}
                                   >
                                      <span className={`
                                         absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-md transition-all duration-300
                                         ${isEnabled ? 'translate-x-6' : 'translate-x-0'}
                                      `}></span>
                                   </button>
                                </td>
                             );
                          })}
                       </tr>
                    ))}
                 </tbody>
              </table>
           </div>
        </div>
      )}

      {activeTab === 'users' && (
         <div className="bg-white dark:bg-[#1a1a20] rounded-[24px] border border-slate-200 dark:border-white/5 overflow-hidden shadow-sm">
            <div className="p-6 border-b border-slate-100 dark:border-white/5 flex justify-between items-center flex-wrap gap-4">
               <div>
                  <h3 className="text-lg font-black text-slate-800 dark:text-white">{isRtl ? 'إدارة المستخدمين' : 'User Management'}</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{isRtl ? 'تخصيص الصلاحيات والأدوار للمستخدمين.' : 'Assign roles and override permissions.'}</p>
               </div>
               <div className="relative">
                  <input 
                     type="text" 
                     placeholder={isRtl ? 'بحث عن مستخدم...' : 'Search user...'} 
                     value={searchUser}
                     onChange={(e) => setSearchUser(e.target.value)}
                     className="bg-slate-50 dark:bg-black/20 border border-slate-200 dark:border-white/10 rounded-xl pl-10 pr-4 py-2 text-xs font-bold dark:text-white outline-none focus:border-emerald-500 w-64"
                  />
                  <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs"></i>
               </div>
            </div>

            <div className="p-6 grid grid-cols-1 gap-4">
               {users.filter(u => u.name.toLowerCase().includes(searchUser.toLowerCase()) || u.email.toLowerCase().includes(searchUser.toLowerCase())).map(user => (
                  <div key={user.id} className="flex flex-col md:flex-row items-center gap-4 p-4 rounded-2xl bg-slate-50 dark:bg-black/20 border border-slate-100 dark:border-white/5">
                     <div className="w-12 h-12 rounded-xl bg-slate-200 dark:bg-white/10 flex items-center justify-center text-slate-500 font-bold overflow-hidden shadow-inner">
                        {user.avatar ? <img src={user.avatar} className="w-full h-full object-cover" /> : user.name.charAt(0)}
                     </div>
                     <div className="flex-1 text-center md:text-start">
                        <h4 className="text-sm font-black dark:text-white">{user.name}</h4>
                        <p className="text-xs text-slate-500">{user.email}</p>
                     </div>
                     
                     {/* Plan Control */}
                     <div className="flex flex-col gap-1 w-full md:w-auto">
                        <label className="text-[9px] font-bold text-slate-400 uppercase">{isRtl ? 'الباقة' : 'Plan'}</label>
                        <select 
                           value={user.plan} 
                           onChange={(e) => handleUserPlanChange(user.id, e.target.value as any)}
                           className="bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-xs font-bold dark:text-white outline-none cursor-pointer hover:border-emerald-500 transition-colors"
                        >
                           <option value="free">Free</option>
                           <option value="pro">Pro</option>
                           <option value="enterprise">Enterprise</option>
                        </select>
                     </div>

                     {/* Explicit Role Control */}
                     <div className="flex flex-col gap-1 w-full md:w-auto">
                        <label className="text-[9px] font-bold text-slate-400 uppercase">{isRtl ? 'الصلاحية (الرول)' : 'System Role'}</label>
                        <div className="flex items-center gap-1 bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg p-1">
                           {(['user', 'admin', 'super', 'superadmin'] as UserRole[]).map(role => (
                              <button 
                                 key={role}
                                 onClick={() => handleUserRoleChange(user.id, role)}
                                 className={`
                                    px-3 py-1.5 rounded-md text-[10px] font-black uppercase transition-all
                                    ${user.role === role 
                                       ? (role === 'user' ? 'bg-slate-200 dark:bg-white/20 text-slate-800 dark:text-white' : 
                                          role === 'admin' ? 'bg-blue-500 text-white shadow-sm' : 
                                          role === 'super' ? 'bg-purple-500 text-white shadow-sm' : 
                                          'bg-emerald-500 text-white shadow-sm')
                                       : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}
                                 `}
                              >
                                 {role === 'superadmin' ? 'S.Admin' : role}
                              </button>
                           ))}
                        </div>
                     </div>

                     <button className="w-full md:w-auto px-4 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl text-xs font-black transition-transform active:scale-95 shadow-lg">
                        <i className="fas fa-id-card mr-2"></i> {isRtl ? 'الملف' : 'Profile'}
                     </button>
                  </div>
               ))}
            </div>
         </div>
      )}
    </div>
  );
};

export default AdminPermissions;
